<script setup lang="ts">
import { useI18N } from '@dialpad/i18n';
import { DATA_QA } from './dnc-name-input.constants';
import { DtInput } from '@dialpad/dialtone/vue3/lib/input';
import { computed, inject, ref, watch } from 'vue';
import type { Ref } from 'vue';
import { TRIGGER_FORM_VALIDATION } from '@/app/utils/keys';
import logger from '@/logger';

const model = defineModel<string>({
  required: true,
});
type Props = {
  label?: string;
  placeholder?: string;
  showDescription?: boolean;
};
const props = withDefaults(defineProps<Props>(), {
  label: '',
  placeholder: '',
  showDescription: true,
});

const log = logger.getChild('DncNameInput');
const { $t } = useI18N();

const validationTrigger = inject<Ref<number>>(TRIGGER_FORM_VALIDATION, ref(0));

enum DncNameErrorType {
  REQUIRED = 'REQUIRED',
  OVER_LIMIT = 'OVER_LIMIT',
}
const emit =
  defineEmits<(e: 'update:error', value: DncNameErrorType | null) => void>();

const validationError = ref<DncNameErrorType | null>(null);

watch(model, () => {
  validationError.value = null;
});

watch(validationError, () => {
  log.debug('Validation error update', validationError.value);
  emit('update:error', validationError.value);
});

const validate = () => {
  if (model.value.length === 0) {
    validationError.value = DncNameErrorType.REQUIRED;
    return;
  }

  if (model.value.length > validateData.value.length.max) {
    validationError.value = DncNameErrorType.OVER_LIMIT;
    return;
  }

  validationError.value = null;
};

watch(validationTrigger, () => {
  log.debug('Validation trigger');
  validate();
});

const validateData = computed(() => ({
  length: {
    description: props.showDescription
      ? $t('DNC_NAME_INPUT_DESCRIPTION')
      : $t('DNC_NAME_INPUT_MAX_CHARACTERS'),
    max: 50,
    warn: 30,
    limitMaxLength: false,
  },
}));

const validationMessage = () => {
  const remainingCharacters =
    validateData.value.length.max - model.value.length;

  if (remainingCharacters < 0) {
    return $t('DNC_NAME_INPUT_OVER_LIMIT', {
      count: Math.abs(remainingCharacters),
    });
  }

  return $t('DNC_NAME_INPUT_CHARACTERS_LEFT', {
    count: remainingCharacters,
  });
};

const inputMessages = computed(() => {
  const messages = [];
  if (validationError.value === DncNameErrorType.REQUIRED) {
    messages.push({ message: $t('DNC_NAME_INPUT_REQUIRED'), type: 'error' });
  }

  if (validationError.value === DncNameErrorType.OVER_LIMIT) {
    messages.push({
      message: $t('DNC_NAME_INPUT_OVER_LIMIT', {
        count: Math.abs(validateData.value.length.max - model.value.length),
      }),
      type: 'error',
    });
  }

  if (
    !validationError.value &&
    model.value.length > validateData.value.length.warn
  ) {
    messages.push({
      message: validationMessage(),
      type: 'warning',
    });
  }

  return messages;
});

const characterCountMessage = computed(() => {
  if (validationError.value) {
    return '';
  }

  const remainingCharacters =
    validateData.value.length.max - model.value.length;
  return $t('DNC_NAME_INPUT_CHARACTERS_LEFT', {
    count: remainingCharacters,
  });
});

const showCharacterCountWithoutError = computed(() => {
  return (
    model.value.length > validateData.value.length.warn &&
    !validationError.value
  );
});
</script>
<template>
  <div :data-qa="DATA_QA.ROOT">
    <DtInput
      v-model="model"
      :data-qa="DATA_QA.INPUT"
      :label="props.label || $t('DNC_NAME_INPUT_LABEL')"
      :placeholder="props.placeholder || $t('DNC_NAME_INPUT_PLACEHOLDER')"
      :messages="inputMessages"
      :maxlength="
        validateData.length.limitMaxLength ? validateData.length.max : null
      "
      required
    />
    <div v-if="props.showDescription">
      <div class="d-d-flex d-jc-space-between d-ai-center">
        <p class="d-fs-100 d-fc-tertiary d-mt4">
          {{ validateData.length.description }}
        </p>
        <p
          v-if="showCharacterCountWithoutError"
          class="d-fs-100 d-fc-tertiary d-mt4"
          :data-qa="DATA_QA.CHARACTER_COUNT"
        >
          {{ characterCountMessage }}
        </p>
      </div>
    </div>
  </div>
</template>
